import React, { useEffect, useRef, useState } from 'react';
import { FiMoreVertical } from 'react-icons/fi';

interface HeaderActionsMenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
}

interface HeaderActionsMenuProps {
  buttonAriaLabel: string;
  items: HeaderActionsMenuItem[];
}

function HeaderActionsMenu({ buttonAriaLabel, items }: HeaderActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        aria-label={buttonAriaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="p-2 rounded-full hover:bg-muted transition-colors"
      >
        <FiMoreVertical size={20} />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-xl border border-border bg-background shadow-lg z-20 p-1">
          {items.map((item) => (
            <React.Fragment key={item.label}>
              {item.separatorBefore && (
                <div className="my-1 border-t border-border" role="separator" />
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  item.onSelect();
                }}
                disabled={item.disabled}
                className={`w-full px-4 py-3 text-left text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  item.destructive
                    ? 'text-error hover:bg-error/10'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default HeaderActionsMenu;
